/* @jsx h */
import { defineComponent, h, ref } from "vue";
import {
  ResponsiveTable,
  RelativeTimeLabel,
  DateTimeRenderer,
  useLocalization,
} from "@lemmonhq/uikit";
import { TransactionDetails } from "./TransactionDetails";
import type { ResponsiveTableColumn } from "@lemmonhq/uikit";
import { formatPriceInLocale, getOrderDisplayName } from "@lemmonhq/orders";
import { Payment, RawPayment } from "@lemmonhq/types";
import { useShop } from "@app/vue/providers/ShopConnection";
import { useAuth } from "@app/vue/providers/Auth";

export interface TransactionListProps {
  transactions: Payment[];
  onPrintReceipt: (paymentId: number) => void;
  onShowDigitalReceipt: (payment: RawPayment) => void;
  onShowCreateCreditNode: (paymentId: number) => void;
  onShowEditPayment: (payment: RawPayment) => void;
  onShowVoidPayment?: (payment: RawPayment) => void;
  onShowReopenTable?: (payment: RawPayment) => void;
  onPaymentShow: (payment: RawPayment) => void;
  showDetailsInModal?: boolean;
  loading?: boolean;
  loadingMore?: boolean;
}

function getPaymentOrders(payment: Payment) {
  return (
    !payment.order && !payment.root_table_orders
      ? []
      : payment.root_table_orders || [payment.order]
  ).filter((el: any) => el && el.display_id);
}

// Pill-free status chip: soft tinted background + matching text, square-ish rounding
const STATUS_COLORS: Record<string, string> = {
  success: "bg-success/15 text-success",
  voided: "bg-error/15 text-error",
  authorized: "bg-warning/15 text-warning",
  pending: "bg-warning/15 text-warning",
  cancelled: "bg-base-content/8 text-base-content/50",
  canceled: "bg-base-content/8 text-base-content/50",
  failed: "bg-error/15 text-error",
  expired: "bg-error/15 text-error",
  sent: "bg-warning/15 text-warning",
};

function statusChip(status: string) {
  const colors =
    STATUS_COLORS[status.toLowerCase()] ?? "bg-warning/15 text-warning";
  return (
    <span>
      <span
        class={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold uppercase  ${colors}`}
      >
        {status}
      </span>
    </span>
  );
}

export const TransactionList = defineComponent<TransactionListProps>({
  name: "TransactionList",
  props: [
    "transactions",
    "onPrintReceipt",
    "onShowDigitalReceipt",
    "onShowCreateCreditNode",
    "onShowEditPayment",
    "onShowVoidPayment",
    "onShowReopenTable",
    "onPaymentShow",
    "showDetailsInModal",
    "loading",
    "loadingMore",
  ],
  setup(props) {
    const auth = useAuth();
    const { t } = useLocalization();
    const { tables } = useShop();

    const columns: ResponsiveTableColumn[] = [
      // ═══════════════════════════════════════════════════════════════════════
      // DESKTOP columns — merged pairs, hidden on mobile
      // ═══════════════════════════════════════════════════════════════════════

      // ── Desktop: Order + Table ────────────────────────────────────────────
      {
        key: "d-order-table",
        label: t("reports.tx.list.column_order"),
        icon: "receipt_long",
        mobilePriority: "hidden",
        render: (payment: Payment) => {
          const orders = getPaymentOrders(payment);
          const label =
            orders.map((o: any) => getOrderDisplayName(o)).join(", ") ||
            `#P${payment.id}`;
          const ts = payment.table_id
            ? tables.value.find((t) => t.id === payment.table_id)
            : null;
          return (
            <div class="flex flex-col gap-0.5">
              <a
                href="#"
                class="font-medium text-accent hover:underline underline-offset-2 truncate max-w-[180px]"
                title={label}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  props.onPaymentShow(payment);
                }}
              >
                {label}
              </a>
              {ts ? (
                <span class="text-sm text-base-content/60 truncate">
                  {ts.table.name}
                </span>
              ) : (
                <span class="text-sm text-base-content/25">—</span>
              )}
            </div>
          );
        },
      },

      // ── Desktop: Amount + Method ──────────────────────────────────────────
      {
        key: "d-amount-method",
        label: t("reports.tx.list.column_amount"),
        icon: "payments",
        mobilePriority: "hidden",
        cellClass: "tabular-nums",
        render: (payment: Payment) => (
          <div class="flex flex-col gap-0.5">
            <span class="font-semibold text-accent">
              {formatPriceInLocale(
                payment.amount + (payment.tips || 0),
                true,
                2,
                auth.user.value?.restaurant.currency || "CHF",
              )}
            </span>
            <span class="text-sm text-base-content/60 capitalize">
              {payment.method || "—"}
            </span>
          </div>
        ),
      },

      // ── Desktop: Status + Time ────────────────────────────────────────────
      {
        key: "d-status-time",
        label: t("reports.tx.list.column_status"),
        mobilePriority: "hidden",
        render: (payment: Payment) => (
          <div class="flex flex-col gap-1">
            {statusChip(payment.status)}
            <DateTimeRenderer
              datetime={payment.created_at}
              class="text-sm text-base-content/60"
            />
          </div>
        ),
      },

      // ═══════════════════════════════════════════════════════════════════════
      // MOBILE-ONLY columns — individual fields for the card layout
      // ═══════════════════════════════════════════════════════════════════════

      {
        key: "m-orders",
        label: t("reports.tx.list.column_orders"),
        desktopHide: true,
        mobilePriority: "primary",
        render: (payment: Payment) => {
          const orders = getPaymentOrders(payment);
          const label =
            orders.map((o: any) => getOrderDisplayName(o)).join(", ") ||
            `#P${payment.id}`;
          return (
            <a
              href="#"
              class="font-medium text-accent hover:underline underline-offset-2 truncate block"
              title={label}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                props.onPaymentShow(payment);
              }}
            >
              {label}
            </a>
          );
        },
      },

      {
        key: "m-amount-status",
        label: t("reports.tx.list.column_amount"),
        desktopHide: true,
        mobilePriority: "primary",
        mobilePrimaryClass:
          "flex-1 min-w-0 flex items-center justify-between gap-1",
        render: (payment: Payment) => (
          <>
            <span class="font-semibold text-accent tabular-nums">
              {formatPriceInLocale(
                payment.amount + (payment.tips || 0),
                true,
                2,
                auth.user.value?.restaurant.currency || "CHF",
              )}
            </span>
            {statusChip(payment.status)}
          </>
        ),
      },

      {
        key: "m-table",
        label: t("reports.tx.list.column_table"),
        desktopHide: true,
        mobilePriority: "secondary",
        render: (payment: Payment) => {
          const ts = payment.table_id
            ? tables.value.find((t) => t.id === payment.table_id)
            : null;
          return ts ? (
            <span>{ts.table.name}</span>
          ) : (
            <span class="text-base-content/25">—</span>
          );
        },
      },

      {
        key: "m-method",
        label: t("reports.tx.list.column_method"),
        desktopHide: true,
        mobilePriority: "secondary",
        render: (payment: Payment) => (
          <span class="capitalize">{payment.method || "—"}</span>
        ),
      },

      // Type — shown on both desktop and mobile
      {
        key: "type",
        label: t("reports.tx.list.column_type"),
        icon: "category",
        mobilePriority: "secondary",
        render: (payment: Payment) => {
          if (payment.type === "credit") {
            return (
              <span class="inline-flex items-center gap-1 text-xs text-error font-medium">
                <span class="material-symbols-outlined text-sm">undo</span>
                {t("reports.tx.list.refund_badge")}
              </span>
            );
          }
          const orders = getPaymentOrders(payment);
          const methods = Array.from(
            new Set(orders.map((o: any) => o.service_method).filter(Boolean)),
          );
          return methods.length ? (
            <span class="capitalize">{methods.join(", ")}</span>
          ) : (
            <span class="text-base-content/25">—</span>
          );
        },
      },

      {
        key: "m-time",
        label: t("reports.tx.list.column_time"),
        desktopHide: true,
        mobilePriority: "secondary",
        render: (payment: Payment) => (
          <DateTimeRenderer datetime={payment.created_at} />
        ),
      },

      // ═══════════════════════════════════════════════════════════════════════
      // ACTIONS — shared (desktop + mobile)
      // ═══════════════════════════════════════════════════════════════════════
      {
        key: "actions",
        label: "",
        mobilePriority: "hidden",
        headerClass: "w-px",
        cellClass: "text-right whitespace-nowrap",
        render: (payment: Payment) => {
          // A credit note can only be voided — printing, refunding, editing and
          // reopening a table all belong to the sale it was issued against.
          if (payment.type === "credit") {
            if (payment.status === "voided" || !props.onShowVoidPayment)
              return null;
            return (
              <div
                class="flex items-center justify-end gap-1.5"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  class="btn btn-error"
                  title={t("reports.tx.void_payment.action_credit")}
                  onClick={() => props.onShowVoidPayment?.(payment)}
                >
                  <span class="material-symbols-outlined">block</span>
                </button>
              </div>
            );
          }

          if (payment.type !== "sale") return null;

          return (
            <div
              class="flex items-center justify-end gap-1.5"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                class="btn btn-primary"
                title={t("general.words.print")}
                onClick={() => props.onPrintReceipt(payment.id)}
              >
                <span class="material-symbols-outlined">print</span>
              </button>
              <button
                class="btn btn-secondary"
                title={t("reports.tx.nav.refund")}
                onClick={() => props.onShowCreateCreditNode(payment.id)}
              >
                <span class="material-symbols-outlined">undo</span>
              </button>
              <button
                class="btn btn-secondary"
                title={t("general.words.edit")}
                onClick={() => props.onShowEditPayment(payment)}
              >
                <span class="material-symbols-outlined">edit_square</span>
              </button>
              {/* More options dropdown */}
              {payment.status !== "voided" && (
                <div class="dropdown dropdown-left dropdown-end">
                  <button
                    tabindex={0}
                    class="btn btn-secondary"
                    title="More options"
                  >
                    <span class="material-symbols-outlined">more_horiz</span>
                  </button>
                  <ul
                    tabindex={0}
                    class="dropdown-content menu bg-base-100 border border-base-content/10 rounded-xl z-20 w-48 p-1 shadow-xl"
                  >
                    <li>
                      <a
                        href="#"
                        class="rounded-lg py-2.5 text-sm"
                        onClick={(e) => {
                          e.preventDefault();
                          props.onShowDigitalReceipt(payment);
                          (e.currentTarget as HTMLElement).blur();
                        }}
                      >
                        <span class="material-symbols-outlined text-info text-base">
                          forward_to_inbox
                        </span>
                        {t("reports.tx.list.send_receipt")}
                      </a>
                    </li>
                    {payment.table_id ? (
                      <li>
                        <a
                          href="#"
                          class="rounded-lg py-2.5 text-sm"
                          onClick={(e) => {
                            e.preventDefault();
                            props.onShowReopenTable?.(payment);
                            (e.currentTarget as HTMLElement).blur();
                          }}
                        >
                          <span class="material-symbols-outlined text-warning text-base">
                            table_restaurant
                          </span>
                          {t("reports.tx.list.reopen_table")}
                        </a>
                      </li>
                    ) : null}
                    <li>
                      <a
                        href="#"
                        class="rounded-lg py-2.5 text-sm text-error"
                        onClick={(e) => {
                          e.preventDefault();
                          props.onShowVoidPayment?.(payment);
                          (e.currentTarget as HTMLElement).blur();
                        }}
                      >
                        <span class="material-symbols-outlined text-base">
                          block
                        </span>
                        {t("reports.tx.list.void_payment")}
                      </a>
                    </li>
                  </ul>
                </div>
              )}{" "}
            </div>
          );
        },
      },
    ];

    const selectedMobilePayment = ref<Payment | null>(null);

    return () => (
      <>
        <ResponsiveTable
          columns={columns}
          rows={props.transactions}
          rowKey={(p: Payment) => p.id}
          onRowClick={(p: Payment) => props.onPaymentShow(p)}
          onMobileCardClick={(p: Payment) => (selectedMobilePayment.value = p)}
          loading={props.loading}
          loadingMore={props.loadingMore}
          emptyMessage={t("reports.tx.list.no_transactions")}
        />
        {selectedMobilePayment.value && (
          <TransactionDetails
            payment={selectedMobilePayment.value}
            onPrintReceipt={props.onPrintReceipt}
            onShowDigitalReceipt={props.onShowDigitalReceipt}
            onShowEditPayment={props.onShowEditPayment}
            onShowVoidPayment={props.onShowVoidPayment}
            onShowReopenTable={props.onShowReopenTable}
            onShowCreateCreditNode={props.onShowCreateCreditNode}
            onClose={() => (selectedMobilePayment.value = null)}
          />
        )}
      </>
    );
  },
});
